
import { useEffect, useMemo, useState } from 'react';

// image markers
import pngLoading from '../../../assets/markers/loading_32.png';
import pngClockWarning from '../../../assets/markers/clock_warning2_32.png';
//import pngHighlight from '../../../assets/markers/highlight_transparent_150.png';
const imgLoading = new Image(); imgLoading.src = pngLoading;
const imgClockWarning = new Image(); imgClockWarning.src = pngClockWarning;

//const imgHighlight = new Image(); imgHighlight.src = pngHighlight;

const useNodeHighlights = (graphData, rawGraphSelection, highlightNeighbours, searchedString, searchInInfo, timeFilter) => {
    const [highlightedNodesSelected, setHighlightedNodesSelected] = useState([]);
    const [highlightedNodesSelectedNeighbour, setHighlightedNodesSelectedNeighbour] = useState([]);
    const [highlightedNodesSearch, setHighlightedNodesSearch] = useState([]);
    const [highlightedNodesSearchFocus, setHighlightedNodesSearchFocus] = useState([]);
    const [highlightedNodesLoading, setHighlightedNodesLoading] = useState([]);
    const [highlightedNodesSearchedLowInterval, setHighlightedNodesSearchedLowInterval] = useState([]);

    function hlDrawSelected(ctx, x, y) {
        ctx.strokeStyle = "#A6D5F7";
        ctx.fillStyle = "#a9a4f5";

        ctx.beginPath();
        ctx.arc(
            x,
            y,
            50,
            0,
            2 * Math.PI,
            false
        );
        ctx.closePath();

        ctx.fill();
        ctx.stroke();
    }

    function hlDrawSelectedNeighbour(ctx, x, y) {
        ctx.strokeStyle = "#3875a2";
        ctx.fillStyle = "#3c3967";

        ctx.beginPath();
        // draw a square around x,y
        ctx.rect(
            x - 80,
            y - 80,
            160,
            160
        );
        ctx.closePath();

        ctx.fill();
        ctx.stroke();
    }

    function hlDrawSearch(ctx, x, y) {
        ctx.strokeStyle = "#2dac51";
        ctx.fillStyle = "#2a4b1d";

        ctx.beginPath();
        ctx.arc(
            x,
            y,
            50,
            0,
            2 * Math.PI,
            false
        );
        ctx.closePath();

        ctx.fill();
        ctx.stroke();
    }

    function hlDrawSearchFocus(ctx, x, y) {
        ctx.strokeStyle = "#49e274";
        ctx.fillStyle = "#a4ff7f";

        ctx.beginPath();
        ctx.arc(
            x,
            y,
            50,
            0,
            2 * Math.PI,
            false
        );
        ctx.closePath();

        ctx.fill();
        ctx.stroke();
    }

    function hlDrawLoading(ctx, x, y) {
        ctx.drawImage(imgLoading, x - 32, y - 32);
    }

    function hlDrawSearchedLowInterval(ctx, x, y) {
        ctx.drawImage(imgClockWarning, x, y - 32);
    }

    useEffect(() => {
        let selectedNodes = rawGraphSelection?.nodes || [];
        let selectedNodeMap = {};
        for (let node of selectedNodes) {
            if (!(node.type in selectedNodeMap)) selectedNodeMap[node.type] = {};
            selectedNodeMap[node.type][node.value] = true;
        }
        let selectedNeighbours = [];
        if (highlightNeighbours && selectedNodes.length === 1) {
            for (let edge of graphData?.edges || []) {
                if (selectedNodeMap[edge.from_type]?.[edge.from_value] && !selectedNodeMap[edge.to_type]?.[edge.to_value]) {
                    selectedNeighbours.push({type: edge.to_type, value: edge.to_value});
                }
                if (selectedNodeMap[edge.to_type]?.[edge.to_value] && !selectedNodeMap[edge.from_type]?.[edge.from_value]) {
                    selectedNeighbours.push({type: edge.from_type, value: edge.from_value});
                }
            }
        }
        //setHighlightedNodesSelected(selectedNodes);
        setHighlightedNodesSelectedNeighbour(selectedNeighbours);
    }, [graphData, rawGraphSelection, highlightNeighbours]);

    useEffect(() => {
        //console.log("UPDATING SEARCHED NODES");
        const updateSearchedNodes = () => {
            if (!searchedString || !graphData?.nodes) { setHighlightedNodesSearch([]); return; }

            const matchedNodesLabel = graphData.nodes.filter(node => {
                if (node.value.toLowerCase().includes(searchedString.toLowerCase())) return true;
                if (node.label && node.label.toLowerCase().includes(searchedString.toLowerCase())) return true;
                if (node.visjsProps?.label && node.visjsProps.label.toLowerCase().includes(searchedString.toLowerCase())) return true;
                return false;
            });

            const matchedNodesInfoNoLabel = graphData.nodes.filter(node => {
                if (matchedNodesLabel.indexOf(node) !== -1) return false;
                if (!searchInInfo) return false;

                if (node.notes) {
                    for (let noteId in node.notes) {
                        if (node.notes[noteId].deleted) continue;
                        if (node.notes[noteId].content && node.notes[noteId].content.toLowerCase().includes(searchedString.toLowerCase())) return true;
                    }
                }
                if (node.info) {
                    for (let info of node.info) {
                        if (!info.content) continue;
                        for (let infoContent of info.content) {
                            if (infoContent?.comment && infoContent.comment.toLowerCase().includes(searchedString.toLowerCase())) return true;
                        }
                    }
                }
                return false;
            });
            let matchedNodes = [...matchedNodesLabel, ...matchedNodesInfoNoLabel];
            setHighlightedNodesSearch(matchedNodes);
        }
        updateSearchedNodes();

        const updateLoadingNodes = () => {
            if (!graphData?.nodes) { setHighlightedNodesLoading([]); return; }

            const loadingNodes = graphData.nodes.filter(node => {
                if (!node.trace) return false;
                for (let msId in node.trace) {
                    if (!node.trace[msId]) return false;
                    for (let fetcherName in node.trace[msId]) {
                        if (node.trace[msId][fetcherName]?.run === 2) return true;
                    }
                }
                return false;
            });
            setHighlightedNodesLoading(loadingNodes);
        }
        updateLoadingNodes();
        
        const updateSearchedOnLowIntervalNodes = () => {
            if (!graphData?.nodes) { setHighlightedNodesSearchedLowInterval([]); return; }
            const nodes = graphData.nodes.filter(node => {
                if (!node.trace) return false;
                for (let msId in node.trace) {
                    if (!node.trace[msId]) continue;
                    for (let fetcherName in node.trace[msId]) {
                        const timeInterval = node.trace[msId][fetcherName]?.timeInterval;
                        if (!timeInterval) continue;
                        if (timeInterval.from > timeFilter.from || timeInterval.to < timeFilter.to) return true;
                    }
                }
                return false;
            });
            setHighlightedNodesSearchedLowInterval(nodes);
        }
        updateSearchedOnLowIntervalNodes();

    }, [graphData, searchedString, searchInInfo, timeFilter]);

    return {
        highlightedNodes: [
            {
                nodes: highlightedNodesSelectedNeighbour,
                inBackground: true,
                drawFunction: hlDrawSelectedNeighbour
            },
            {
                nodes: highlightedNodesSelected,
                inBackground: true,
                drawFunction: hlDrawSelected
            },
            {
                nodes: highlightedNodesSearch,
                inBackground: true,
                drawFunction: hlDrawSearch
            },
            {
                nodes: highlightedNodesSearchFocus,
                inBackground: true,
                drawFunction: hlDrawSearchFocus
            },
            {
                nodes: highlightedNodesLoading,
                inBackground: false,
                drawFunction: hlDrawLoading
            },
            {
                nodes: highlightedNodesSearchedLowInterval,
                inBackground: false,
                drawFunction: hlDrawSearchedLowInterval
            }
        ],
        highlightedNodesSelected,
        highlightedNodesSearch,
        highlightedNodesSearchFocus,
        highlightedNodesLoading,
        highlightedNodesSearchedLowInterval,
        setHighlightedNodesSelected,
        setHighlightedNodesSearch,
        setHighlightedNodesSearchFocus,
        setHighlightedNodesLoading,
        setHighlightedNodesSearchedLowInterval
    };
}

export { useNodeHighlights };
